import { useState } from "react";
import { X, Wand2, Loader2, Sparkles } from "lucide-react";
import { useGenerateSection } from "@/hooks/use-proposals";

interface AIGenerateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  proposalId: string;
  sectionId: string;
  sectionTitle: string;
}

/**
 * Diálogo para generar contenido de una sección con IA (flujo ASÍNCRONO).
 *
 * La generación (writer + loop crítico-escritor) puede tardar minutos, así que
 * el backend responde 202 y la corre en segundo plano. Este diálogo solo lanza
 * la generación y se cierra: la sección queda "Generando…" en el índice/editor
 * y, cuando termina, el contenido aparece solo (la página sondea el estado).
 * Esto elimina la petición HTTP larga que Envoy cortaba a los 120s produciendo
 * un falso "Error al generar la sección".
 */
export function AIGenerateDialog({
  isOpen,
  onClose,
  proposalId,
  sectionId,
  sectionTitle,
}: AIGenerateDialogProps) {
  const [instructions, setInstructions] = useState("");
  // Mejora A (changelog cliente): longitud objetivo opcional. Vacío = sin
  // restricción (usa el tope dinámico por tipo de sección del backend).
  const [targetWordsInput, setTargetWordsInput] = useState("");
  const [targetWordsError, setTargetWordsError] = useState<string | null>(null);

  const generateMutation = useGenerateSection(proposalId);

  const resetAndClose = () => {
    setInstructions("");
    setTargetWordsInput("");
    setTargetWordsError(null);
    generateMutation.reset();
    onClose();
  };

  const handleGenerate = async () => {
    // Validamos target_words antes de mandar: el backend exige 50-10000, pero
    // damos feedback inmediato al usuario en lugar de esperar al 422.
    const trimmedTarget = targetWordsInput.trim();
    let targetWords: number | undefined;
    if (trimmedTarget) {
      const parsed = Number.parseInt(trimmedTarget, 10);
      if (Number.isNaN(parsed) || parsed < 50 || parsed > 10000) {
        setTargetWordsError("La longitud objetivo debe estar entre 50 y 10 000 palabras.");
        return;
      }
      targetWords = parsed;
    }
    setTargetWordsError(null);

    try {
      await generateMutation.mutateAsync({
        sectionId,
        instructions: instructions.trim() || undefined,
        target_words: targetWords,
      });
      // 202 recibido: la generación corre en segundo plano. Cerramos; la
      // sección aparece "Generando…" y el contenido llega vía polling.
      resetAndClose();
    } catch {
      // El hook ya muestra el toast de error de arranque; dejamos el diálogo
      // abierto para reintentar.
    }
  };

  // No cerrar mientras se lanza el arranque (muy breve): evita dobles envíos.
  const handleClose = () => {
    if (generateMutation.isPending) return;
    resetAndClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Generar contenido para ${sectionTitle}`}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900">Generar con IA</h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            Sección: <span className="font-medium text-gray-900">{sectionTitle}</span>
          </p>

          <div>
            <label
              htmlFor="ai-instructions"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Instrucciones adicionales (opcional)
            </label>
            <textarea
              id="ai-instructions"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Ej: Enfocarse en la experiencia previa del equipo, incluir métricas concretas..."
              rows={4}
              disabled={generateMutation.isPending}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none disabled:opacity-50"
            />
          </div>

          {/* Mejora A (changelog cliente): longitud objetivo opcional */}
          <div>
            <label
              htmlFor="ai-target-words"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Longitud objetivo (palabras){" "}
              <span className="text-gray-400 font-normal">opcional</span>
            </label>
            <input
              id="ai-target-words"
              type="number"
              inputMode="numeric"
              min={50}
              max={10000}
              step={50}
              value={targetWordsInput}
              onChange={(e) => {
                setTargetWordsInput(e.target.value);
                if (targetWordsError) setTargetWordsError(null);
              }}
              placeholder="Ej: 800"
              disabled={generateMutation.isPending}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50"
              aria-describedby="ai-target-words-help"
              aria-invalid={!!targetWordsError}
            />
            <p id="ai-target-words-help" className="mt-1 text-xs text-gray-500">
              Si lo indicas, la IA ajustará el detalle para acercarse a esa
              longitud sin omitir requisitos obligatorios del pliego.
            </p>
            {targetWordsError && (
              <p className="mt-1 text-xs text-red-600" role="alert">
                {targetWordsError}
              </p>
            )}
          </div>

          {/* Aviso: la generación corre en segundo plano. */}
          <div
            className="flex items-start gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-md text-sm text-purple-800"
            role="note"
          >
            <Sparkles className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
            <span>
              La sección se genera en segundo plano (writer + agente crítico) y
              puede tardar uno o dos minutos. Puedes cerrar esta ventana y seguir
              trabajando: el contenido aparecerá solo al terminar.
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={generateMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generateMutation.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {generateMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Lanzando…
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4" />
                Generar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AIGenerateDialog;
